import { Wallet, Coins, Gem, Gift, History } from "lucide-react";
import { useLang } from "@/i18n";
import { shortAddress } from "@/config/site";
import { PageHero, Reveal } from "@/components/common";
import { ConnectWalletButton } from "@/components/ConnectWalletButton";
import { useWallet } from "@/wallet/WalletContext";

export default function MyNfts() {
  const { t } = useLang();
  const { connected, address, solBalance, balanceError, providerName } = useWallet();

  return (
    <>
      <PageHero eyebrow={t("nav.nfts")} title={t("mynfts.title")} sub={connected ? undefined : t("mynfts.connect")} />

      <section className="section-pad" data-testid="mynfts-page">
        {!connected ? (
          <Reveal className="card-surface flex flex-col items-start gap-5 p-8">
            <p className="font-heading text-xl font-bold uppercase tracking-wide text-paper" data-testid="mynfts-state">
              {t("mint.state.wallet")}
            </p>
            <ConnectWalletButton />
          </Reveal>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: Wallet, label: providerName || "Wallet", value: shortAddress(address), id: "wallet" },
                { icon: Coins, label: t("mynfts.sol"), value: balanceError ? "RPC unavailable" : solBalance === null ? "…" : `${solBalance.toFixed(4)} SOL`, id: "sol" },
                { icon: Gem, label: t("mynfts.mbga"), value: t("mint.unavailable"), id: "mbga" },
                { icon: Gift, label: t("mynfts.claimable"), value: t("mynfts.none"), id: "claimable" },
              ].map((c) => (
                <Reveal key={c.id}>
                  <div className="card-surface p-5" data-testid={`mynfts-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <c.icon size={15} className="text-goldlight" />
                      <p className="font-heading text-[10px] font-bold uppercase tracking-[0.25em] text-mist">{c.label}</p>
                    </div>
                    <p className="mt-3 font-mono text-sm font-bold text-paper">{c.value}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1} className="mt-8">
              <div className="card-surface p-8" data-testid="mynfts-owned">
                <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-paper">{t("mynfts.owned")}</h2>
                <div className="mt-6 rounded-xl border border-dashed border-white/10 p-12 text-center text-sm text-mist">
                  {t("mynfts.empty")}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-6">
              <div className="card-surface p-8" data-testid="mynfts-history">
                <div className="flex items-center gap-2">
                  <History size={16} className="text-goldlight" />
                  <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-paper">{t("mynfts.history")}</h2>
                </div>
                <div className="mt-6 rounded-xl border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-widest text-mist">
                  —
                </div>
              </div>
            </Reveal>
          </>
        )}
      </section>
    </>
  );
}
